import { useState, useEffect } from 'react';
import { useError } from '../../context/ErrorContext';
import { godrejBarcaMsrService } from '../../services/godrejBacraMsrService';
import { Save, X, PlusCircle, Trash2, ImageIcon, UploadCloud } from 'lucide-react';
import { nanoid } from 'nanoid';

const GodrejBarcaMsrContentManager = ({ setEditingGodrejBarcaMsr, updatePageLastUpdated }) => {
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showError } = useError();

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const content = await godrejBarcaMsrService.getGodrejContent();
        setPageContent(content);
      } catch (error) {
        showError('Failed to load Godrej MSR City page content: ' + error.message, 'error');
        setPageContent(null);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [showError]);

  const handleSaveContent = async (sectionName, content) => {
    try {
      const updatedFullContent = {
        ...pageContent,
        [sectionName]: content
      };

      await godrejBarcaMsrService.createOrUpdateGodrejContent(updatedFullContent);
      showError(`${sectionName} section updated successfully!`, 'success');
      const latestContent = await godrejBarcaMsrService.getGodrejContent();
      setPageContent(latestContent);
      await updatePageLastUpdated('Godrej MSR City');
    } catch (error) {
      showError(`Failed to update ${sectionName} section: ${error.message}`, 'error');
    }
  };

  const handleSaveLocationHighlight = async (highlightIndex, highlightContent) => {
    try {
        const newHighlights = [...pageContent.location.highlights];
        newHighlights[highlightIndex] = highlightContent;
        const updatedLocation = {
            ...pageContent.location,
            highlights: newHighlights
        };
        await handleSaveContent('location', updatedLocation);
    } catch (error) {
        showError('Failed to save location highlight: ' + error.message, 'error');
    }
  };
  
  const handleSaveAmenity = async (amenityIndex, amenityContent) => {
    try {
        const newAmenityList = [...pageContent.amenities.amenityList];
        newAmenityList[amenityIndex] = amenityContent;
        const updatedAmenities = {
            ...pageContent.amenities,
            amenityList: newAmenityList
        };
        await handleSaveContent('amenities', updatedAmenities);
    } catch (error) {
        showError('Failed to save amenity: ' + error.message, 'error');
    }
  };

  const handleChange = (section, field, value) => {
    setPageContent(prevContent => ({
      ...prevContent,
      [section]: {
        ...prevContent[section],
        [field]: value
      }
    }));
  };

  const handleImageUpload = (file, section, field) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const newImageUrl = reader.result;
      setPageContent(prevContent => ({
        ...prevContent,
        [section]: {
          ...prevContent[section],
          [field]: newImageUrl
        }
      }));
    };
    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const handleAddTextParagraph = (section) => {
    setPageContent(prevContent => ({
      ...prevContent,
      [section]: {
        ...prevContent[section],
        text: [...(prevContent[section]?.text || []), '']
      }
    }));
  };

  const handleDeleteTextParagraph = (section, index) => {
    setPageContent(prevContent => {
      const newText = [...(prevContent[section]?.text || [])];
      newText.splice(index, 1);
      return {
        ...prevContent,
        [section]: {
          ...prevContent[section],
          text: newText
        }
      };
    });
  };

  const handleAboutTextChange = (index, value) => {
    setPageContent(prevContent => {
      const newText = [...(prevContent.aboutProject.text || [])];
      newText[index] = value;
      return {
        ...prevContent,
        aboutProject: { ...prevContent.aboutProject, text: newText }
      };
    });
  };

  const handleLocationHighlightChange = (highlightIndex, field, value) => {
    setPageContent(prevContent => ({
      ...prevContent,
      location: {
        ...prevContent.location,
        highlights: prevContent.location.highlights.map((highlight, i) =>
          i === highlightIndex ? { ...highlight, [field]: value } : highlight
        )
      }
    }));
  };

  const handleLocationHighlightPointsChange = (highlightIndex, pointIndex, value) => {
    setPageContent(prevContent => ({
      ...prevContent,
      location: {
        ...prevContent.location,
        highlights: prevContent.location.highlights.map((highlight, hIndex) =>
          hIndex === highlightIndex ? {
            ...highlight,
            points: highlight.points.map((point, pIndex) => pIndex === pointIndex ? value : point)
          } : highlight
        )
      }
    }));
  };

  const handleLocationImageUpload = (file, highlightIndex) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const newImageUrl = reader.result;
      setPageContent(prevContent => ({
        ...prevContent,
        location: {
          ...prevContent.location,
          highlights: prevContent.location.highlights.map((highlight, i) =>
            i === highlightIndex ? { ...highlight, image: newImageUrl } : highlight
          )
        }
      }));
    };
    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const handleMediterraneanTextChange = (index, value) => {
    setPageContent(prevContent => {
      const newText = [...(prevContent.mediterraneanWay.text || [])];
      newText[index] = value;
      return {
        ...prevContent,
        mediterraneanWay: { ...prevContent.mediterraneanWay, text: newText }
      };
    });
  };

  const handleAmenityChange = (amenityIndex, field, value) => {
    setPageContent(prevContent => ({
      ...prevContent,
      amenities: {
        ...prevContent.amenities,
        amenityList: prevContent.amenities.amenityList.map((amenity, i) =>
          i === amenityIndex ? { ...amenity, [field]: value } : amenity
        )
      }
    }));
  };

  const handleAmenityImageUpload = (file, amenityIndex) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const newImageUrl = reader.result;
      setPageContent(prevContent => ({
        ...prevContent,
        amenities: {
          ...prevContent.amenities,
          amenityList: prevContent.amenities.amenityList.map((amenity, i) =>
            i === amenityIndex ? { ...amenity, image: newImageUrl } : amenity
          )
        }
      }));
    };
    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const handleAddAmenity = () => {
    setPageContent(prevContent => ({
      ...prevContent,
      amenities: {
        ...prevContent.amenities,
        amenityList: [...prevContent.amenities.amenityList, { heading: '', text: '', image: '' }]
      }
    }));
  };

  const handleDeleteAmenity = (index) => {
    setPageContent(prevContent => {
      const newAmenityList = [...prevContent.amenities.amenityList];
      newAmenityList.splice(index, 1);
      return {
        ...prevContent,
        amenities: {
          ...prevContent.amenities,
          amenityList: newAmenityList
        }
      };
    });
  };

  const sectionStyles = "bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 transition-all duration-300 hover:shadow-xl";
  const headingStyles = "text-2xl font-extrabold text-gray-900 dark:text-white mb-6 border-b pb-3 border-gray-200 dark:border-gray-700";
  const labelStyles = "block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5";
  const inputStyles = "w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent/30 focus:border-transparent dark:bg-gray-800 transition duration-200 ease-in-out";
  const textareaStyles = "w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-accent/30 focus:border-transparent dark:bg-gray-800 transition duration-200 ease-in-out";
  const buttonStyles = "flex items-center justify-center font-bold px-6 py-3 rounded-xl transition-transform duration-200 ease-in-out transform hover:scale-105 shadow-lg";
  const saveButtonStyles = `${buttonStyles} bg-accent hover:primary-700 text-white hover:bg-primary-700`;
  const closeButtonStyles = `${buttonStyles} bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600`;
  const closeButtonStyle = `${buttonStyles} bg-gray-200 mt-2 text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600`;
  const addButtonStyles = `text-green-500 hover:text-accent/60 font-bold transition-colors duration-200 flex items-center mt-4`;
  const uploadLabelStyles = "flex items-center px-4 py-2  text-accent/80 dark:text-accent/60 rounded-md border border-accent dark:border-accent/40 cursor-pointer hover:bg-accent/20 dark:hover:bg-accent/30 transition duration-200 text-sm font-medium";

  if (loading) {
    return (
       <div className="flex items-center justify-center py-12 bg-white dark:bg-gray-950 rounded-lg shadow-xl dark:shadow-2xl">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-8 w-8 text-accent dark:text-accent mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300">Loading Godrej Bacra Msr City editor...</p>
        </div>
      </div>
    );
  }

  if (!pageContent) {
    return (
      <div className="text-center py-12 bg-red-50 dark:bg-red-950 border-2 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded-2xl shadow-xl mx-auto max-w-2xl mt-12">
        <p className="font-extrabold text-2xl mb-2">Error!</p>
        <p className="text-lg">Could not load Godrej MSR City content for editing. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="mt-8 bg-gray-100 dark:bg-gray-800 p-4 md:p-8 rounded-3xl shadow-2xl dark:shadow-inner border border-gray-200 dark:border-gray-700 animate-fade-in-up">
      <div className="flex justify-between items-center mb-8 border-b-4 pb-4 border-accent dark:border-accent/60">
        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight">
          Edit Godrej MSR City Page Content
        </h2>
        <button
          onClick={() => setEditingGodrejBarcaMsr(false)}
          className={closeButtonStyles}
        >
          <X className="w-5 h-5 mr-2" /> Close
        </button>
      </div>

      <div className="space-y-12">
        {/* Page Title */}
        <div className={sectionStyles}>
          <h3 className={headingStyles}>Page Meta Title</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="pageTitle" className={labelStyles}>Page Title</label>
              <input
                type="text"
                id="pageTitle"
                className={inputStyles}
                value={pageContent?.pageTitle || ''}
                onChange={(e) => setPageContent(prev => ({ ...prev, pageTitle: e.target.value }))}
              />
            </div>
          </div>
          <button
            onClick={() => handleSaveContent('pageTitle', pageContent.pageTitle)}
            className={`${saveButtonStyles} mt-6`}
          >
            <Save className="w-5 h-5 mr-2" /> Save Page Title
          </button>
        </div>

        {/* Hero Section */}
        <div className={sectionStyles}>
          <h3 className={headingStyles}>Hero Section</h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="heroTitle" className={labelStyles}>Title</label>
              <input
                type="text"
                id="heroTitle"
                className={inputStyles}
                value={pageContent?.heroSection?.title || ''}
                onChange={(e) => handleChange('heroSection', 'title', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="heroSubtitle" className={labelStyles}>Subtitle</label>
              <input
                type="text"
                id="heroSubtitle"
                className={inputStyles}
                value={pageContent?.heroSection?.subtitle || ''}
                onChange={(e) => handleChange('heroSection', 'subtitle', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="heroTagline" className={labelStyles}>Tagline</label>
              <input
                type="text"
                id="heroTagline"
                className={inputStyles}
                value={pageContent?.heroSection?.tagline || ''}
                onChange={(e) => handleChange('heroSection', 'tagline', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="heroBgImage" className={labelStyles}>Background Image URL</label>
              {pageContent?.heroSection?.backgroundImage && (
                <div className="mb-4 rounded-xl overflow-hidden shadow-md">
                  <img src={pageContent.heroSection.backgroundImage} alt="Hero Background" className="w-full h-60 object-cover" />
                </div>
              )}
              <input
                type="text"
                id="heroBgImage"
                className={inputStyles}
                value={pageContent?.heroSection?.backgroundImage || ''}
                onChange={(e) => handleChange('heroSection', 'backgroundImage', e.target.value)}
              />
              <div className="mt-4">
                <label htmlFor="heroBgImageUpload" className={uploadLabelStyles}>
                  <UploadCloud className="w-4 h-4 mr-2" /> Upload Image
                </label>
                <input
                  type="file"
                  id="heroBgImageUpload"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0], 'heroSection', 'backgroundImage')}
                />
              </div>
            </div>
          </div>
          <button
            onClick={() => handleSaveContent('heroSection', pageContent.heroSection)}
            className={`${saveButtonStyles} mt-8`}
          >
            <Save className="w-5 h-5 mr-2" /> Save Hero Section
          </button>
        </div>

        {/* About Project Section */}
        <div className={sectionStyles}>
          <h3 className={headingStyles}>About Project</h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="aboutHeading" className={labelStyles}>Heading</label>
              <input
                type="text"
                id="aboutHeading"
                className={inputStyles}
                value={pageContent?.aboutProject?.heading || ''}
                onChange={(e) => handleChange('aboutProject', 'heading', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="aboutImage" className={labelStyles}>Image URL</label>
              {pageContent?.aboutProject?.image && (
                <div className="mb-4 rounded-xl overflow-hidden shadow-md">
                  <img src={pageContent.aboutProject.image} alt="About Project" className="w-full h-60 object-cover" />
                </div>
              )}
              <input
                type="text"
                id="aboutImage"
                className={inputStyles}
                value={pageContent?.aboutProject?.image || ''}
                onChange={(e) => handleChange('aboutProject', 'image', e.target.value)}
              />
              <div className="mt-4">
                <label htmlFor="aboutImageUpload" className={uploadLabelStyles}>
                  <UploadCloud className="w-4 h-4 mr-2" /> Upload Image
                </label>
                <input
                  type="file"
                  id="aboutImageUpload"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0], 'aboutProject', 'image')}
                />
              </div>
            </div>
            <div className="space-y-4">
              <label className={labelStyles}>Paragraphs</label>
              {pageContent?.aboutProject?.text?.map((text, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <textarea
                    id={`aboutText${index}`}
                    rows="3"
                    className={textareaStyles}
                    value={text}
                    onChange={(e) => handleAboutTextChange(index, e.target.value)}
                    placeholder={`Paragraph ${index + 1}`}
                  ></textarea>
                  <button
                    onClick={() => handleDeleteTextParagraph('aboutProject', index)}
                    className="p-2 text-red-500 hover:text-red-700 transition-colors duration-200 flex-shrink-0"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => handleAddTextParagraph('aboutProject')}
                className={addButtonStyles}
              >
                <PlusCircle className="w-5 h-5 mr-2" /> Add Paragraph
              </button>
            </div>
          </div>
          <button
            onClick={() => handleSaveContent('aboutProject', pageContent.aboutProject)}
            className={`${saveButtonStyles} mt-8`}
          >
            <Save className="w-5 h-5 mr-2" /> Save About Project
          </button>
        </div>

        {/* Location Section */}
        <div className={sectionStyles}>
          <h3 className={headingStyles}>Location</h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="locationHeading" className={labelStyles}>Heading</label>
              <input
                type="text"
                id="locationHeading"
                className={inputStyles}
                value={pageContent?.location?.heading || ''}
                onChange={(e) => handleChange('location', 'heading', e.target.value)}
              />
            </div>
            {pageContent?.location?.highlights?.map((highlight, hIndex) => (
              <div key={hIndex} className="p-6 border border-gray-300 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 space-y-4">
                <div className="flex justify-between items-center border-b pb-3 mb-4 border-gray-200 dark:border-gray-700">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-white">Highlight {hIndex + 1}</h4>
                </div>
                <div>
                  <label className={labelStyles}>Title</label>
                  <input
                    type="text"
                    className={inputStyles}
                    value={highlight.title}
                    onChange={(e) => handleLocationHighlightChange(hIndex, 'title', e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelStyles}>Image URL</label>
                  {highlight.image && (
                    <div className="mb-4 rounded-xl overflow-hidden shadow-md">
                      <img src={highlight.image} alt={`Location Highlight ${hIndex + 1}`} className="w-full h-60 object-cover" />
                    </div>
                  )}
                  <input
                    type="text"
                    className={inputStyles}
                    value={highlight.image}
                    onChange={(e) => handleLocationHighlightChange(hIndex, 'image', e.target.value)}
                  />
                  <div className="mt-4">
                    <label htmlFor={`locationImageUpload-${hIndex}`} className={uploadLabelStyles}>
                      <UploadCloud className="w-4 h-4 mr-2" /> Upload Image
                    </label>
                    <input
                      type="file"
                      id={`locationImageUpload-${hIndex}`}
                      className="hidden"
                      onChange={(e) => handleLocationImageUpload(e.target.files[0], hIndex)}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelStyles}>Text</label>
                  <textarea
                    rows="3"
                    className={textareaStyles}
                    value={highlight.text[0]}
                    onChange={(e) => handleLocationHighlightChange(hIndex, 'text', [e.target.value])}
                  ></textarea>
                </div>
                <div>
                  <label className={labelStyles}>Points (One per line)</label>
                  <textarea
                    rows="5"
                    className={textareaStyles}
                    value={highlight.points.join('\n')}
                    onChange={(e) => handleLocationHighlightChange(hIndex, 'points', e.target.value.split('\n'))}
                  ></textarea>
                </div>
                <button
                    onClick={() => handleSaveLocationHighlight(hIndex, highlight)}
                    className={`${saveButtonStyles} mt-6`}
                >
                    <Save className="w-5 h-5 mr-2" /> Save Highlight
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => handleSaveContent('location', pageContent.location)}
            className={`${saveButtonStyles} mt-8`}
          >
            <Save className="w-5 h-5 mr-2" /> Save Location
          </button>
        </div>

        {/* Mediterranean Way Section */}
        <div className={sectionStyles}>
          <h3 className={headingStyles}>Mediterranean Way</h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="mediterraneanHeading" className={labelStyles}>Heading</label>
              <input
                type="text"
                id="mediterraneanHeading"
                className={inputStyles}
                value={pageContent?.mediterraneanWay?.heading || ''}
                onChange={(e) => handleChange('mediterraneanWay', 'heading', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="mediterraneanImage" className={labelStyles}>Image URL</label>
              {pageContent?.mediterraneanWay?.image && (
                <div className="mb-4 rounded-xl overflow-hidden shadow-md">
                  <img src={pageContent.mediterraneanWay.image} alt="Mediterranean Way" className="w-full h-60 object-cover" />
                </div>
              )}
              <input
                type="text"
                id="mediterraneanImage"
                className={inputStyles}
                value={pageContent?.mediterraneanWay?.image || ''}
                onChange={(e) => handleChange('mediterraneanWay', 'image', e.target.value)}
              />
              <div className="mt-4">
                <label htmlFor="mediterraneanImageUpload" className={uploadLabelStyles}>
                  <UploadCloud className="w-4 h-4 mr-2" /> Upload Image
                </label>
                <input
                  type="file"
                  id="mediterraneanImageUpload"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0], 'mediterraneanWay', 'image')}
                />
              </div>
            </div>
            <div className="space-y-4">
              <label className={labelStyles}>Paragraphs</label>
              {pageContent?.mediterraneanWay?.text?.map((text, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <textarea
                    id={`mediterraneanText${index}`}
                    rows="3"
                    className={textareaStyles}
                    value={text}
                    onChange={(e) => handleMediterraneanTextChange(index, e.target.value)}
                    placeholder={`Paragraph ${index + 1}`}
                  ></textarea>
                  <button
                    onClick={() => handleDeleteTextParagraph('mediterraneanWay', index)}
                    className="p-2 text-red-500 hover:text-red-700 transition-colors duration-200 flex-shrink-0"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => handleAddTextParagraph('mediterraneanWay')}
                className={addButtonStyles}
              >
                <PlusCircle className="w-5 h-5 mr-2" /> Add Paragraph
              </button>
            </div>
          </div>
          <button
            onClick={() => handleSaveContent('mediterraneanWay', pageContent.mediterraneanWay)}
            className={`${saveButtonStyles} mt-8`}
          >
            <Save className="w-5 h-5 mr-2" /> Save Mediterranean Way
          </button>
        </div>

        {/* Amenities Section */}
        <div className={sectionStyles}>
          <h3 className={headingStyles}>Amenities</h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="amenitiesHeading" className={labelStyles}>Heading</label>
              <input
                type="text"
                id="amenitiesHeading"
                className={inputStyles}
                value={pageContent?.amenities?.heading || ''}
                onChange={(e) => handleChange('amenities', 'heading', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="amenitiesDescription" className={labelStyles}>Description</label>
              <textarea
                id="amenitiesDescription"
                rows="3"
                className={textareaStyles}
                value={pageContent?.amenities?.description || ''}
                onChange={(e) => handleChange('amenities', 'description', e.target.value)}
              ></textarea>
            </div>
            <button
                onClick={() => handleSaveContent('amenities', pageContent.amenities)}
                className={`${saveButtonStyles} mt-6`}
            >
                <Save className="w-5 h-5 mr-2" /> Save Heading & Description
            </button>
            {pageContent?.amenities?.amenityList?.map((amenity, aIndex) => (
              <div key={aIndex} className="relative p-6 border border-gray-300 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 space-y-4">
                <div className="flex justify-between items-center border-b pb-3 mb-4 border-gray-200 dark:border-gray-700">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-white">Amenity {aIndex + 1}</h4>
                  <button
                    onClick={() => handleDeleteAmenity(aIndex)}
                    className="p-2 text-red-500 hover:text-red-700 transition-colors duration-200"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                <div>
                  <label className={labelStyles}>Heading</label>
                  <input
                    type="text"
                    className={inputStyles}
                    value={amenity.heading}
                    onChange={(e) => handleAmenityChange(aIndex, 'heading', e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelStyles}>Text</label>
                  <textarea
                    rows="3"
                    className={textareaStyles}
                    value={amenity.text}
                    onChange={(e) => handleAmenityChange(aIndex, 'text', e.target.value)}
                  ></textarea>
                </div>
                <div>
                  <label className={labelStyles}>Image URL</label>
                  {amenity.image && (
                    <div className="mb-4 rounded-xl overflow-hidden shadow-md">
                      <img src={amenity.image} alt={`Amenity ${aIndex + 1}`} className="w-full h-60 object-cover" />
                    </div>
                  )}
                  <input
                    type="text"
                    className={inputStyles}
                    value={amenity.image}
                    onChange={(e) => handleAmenityChange(aIndex, 'image', e.target.value)}
                  />
                  <div className="mt-4">
                    <label htmlFor={`amenityImageUpload-${aIndex}`} className={uploadLabelStyles}>
                      <UploadCloud className="w-4 h-4 mr-2" /> Upload Image
                    </label>
                    <input
                      type="file"
                      id={`amenityImageUpload-${aIndex}`}
                      className="hidden"
                      onChange={(e) => handleAmenityImageUpload(e.target.files[0], aIndex)}
                    />
                  </div>
                </div>
                <button
                    onClick={() => handleSaveAmenity(aIndex, amenity)}
                    className={`${saveButtonStyles} mt-6`}
                >
                    <Save className="w-5 h-5 mr-2" /> Save Amenity
                </button>
              </div>
            ))}
            <button onClick={handleAddAmenity} className={addButtonStyles}>
              <PlusCircle className="w-5 h-5 mr-2" /> Add New Amenity
            </button>
          </div>
        </div>

        {/* Site Plan Section */}
        <div className={sectionStyles}>
          <h3 className={headingStyles}>Site Plan</h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="sitePlanHeading" className={labelStyles}>Heading</label>
              <input
                type="text"
                id="sitePlanHeading"
                className={inputStyles}
                value={pageContent?.sitePlan?.heading || ''}
                onChange={(e) => handleChange('sitePlan', 'heading', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="sitePlanDescription" className={labelStyles}>Description</label>
              <textarea
                id="sitePlanDescription"
                rows="3"
                className={textareaStyles}
                value={pageContent?.sitePlan?.description || ''}
                onChange={(e) => handleChange('sitePlan', 'description', e.target.value)}
              ></textarea>
            </div>
            <div>
              <label htmlFor="sitePlanImage" className={labelStyles}>Image URL</label>
              {pageContent?.sitePlan?.image && (
                <div className="mb-4 rounded-xl overflow-hidden shadow-md">
                  <img src={pageContent.sitePlan.image} alt="Site Plan" className="w-full h-60 object-cover" />
                </div>
              )}
              <input
                type="text"
                id="sitePlanImage"
                className={inputStyles}
                value={pageContent?.sitePlan?.image || ''}
                onChange={(e) => handleChange('sitePlan', 'image', e.target.value)}
              />
              <div className="mt-4">
                <label htmlFor="sitePlanImageUpload" className={uploadLabelStyles}>
                  <UploadCloud className="w-4 h-4 mr-2" /> Upload Image
                </label>
                <input
                  type="file"
                  id="sitePlanImageUpload"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0], 'sitePlan', 'image')}
                />
              </div>
            </div>
          </div>
          <button
            onClick={() => handleSaveContent('sitePlan', pageContent.sitePlan)}
            className={`${saveButtonStyles} mt-8`}
          >
            <Save className="w-5 h-5 mr-2" /> Save Site Plan
          </button>
        </div>

        {/* About Developer Section */}
        <div className={sectionStyles}>
          <h3 className={headingStyles}>About Developer</h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="devHeading" className={labelStyles}>Heading</label>
              <input
                type="text"
                id="devHeading"
                className={inputStyles}
                value={pageContent?.aboutDeveloper?.heading || ''}
                onChange={(e) => handleChange('aboutDeveloper', 'heading', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="devSubheading" className={labelStyles}>Subheading</label>
              <input
                type="text"
                id="devSubheading"
                className={inputStyles}
                value={pageContent?.aboutDeveloper?.subheading || ''}
                onChange={(e) => handleChange('aboutDeveloper', 'subheading', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="devDescription" className={labelStyles}>Description</label>
              <textarea
                id="devDescription"
                rows="3"
                className={textareaStyles}
                value={pageContent?.aboutDeveloper?.description || ''}
                onChange={(e) => handleChange('aboutDeveloper', 'description', e.target.value)}
              ></textarea>
            </div>
            <div>
              <label htmlFor="devRecord" className={labelStyles}>Record</label>
              <input
                type="text"
                id="devRecord"
                className={inputStyles}
                value={pageContent?.aboutDeveloper?.record || ''}
                onChange={(e) => handleChange('aboutDeveloper', 'record', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="devLogo" className={labelStyles}>Logo URL</label>
              {pageContent?.aboutDeveloper?.logo && (
                <div className="mb-4 w-32 h-32 rounded-xl overflow-hidden shadow-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  <img src={pageContent.aboutDeveloper.logo} alt="Developer Logo" className="w-full h-auto object-contain p-2" />
                </div>
              )}
              <input
                type="text"
                id="devLogo"
                className={inputStyles}
                value={pageContent?.aboutDeveloper?.logo || ''}
                onChange={(e) => handleChange('aboutDeveloper', 'logo', e.target.value)}
              />
              <div className="mt-4">
                <label htmlFor="devLogoUpload" className={uploadLabelStyles}>
                  <UploadCloud className="w-4 h-4 mr-2" /> Upload Logo
                </label>
                <input
                  type="file"
                  id="devLogoUpload"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0], 'aboutDeveloper', 'logo')}
                />
              </div>
            </div>
          </div>
          <button
            onClick={() => handleSaveContent('aboutDeveloper', pageContent.aboutDeveloper)}
            className={`${saveButtonStyles} mt-8`}
          >
            <Save className="w-5 h-5 mr-2" /> Save About Developer
          </button>
        </div>

        {/* Pre-Book Section */}
        <div className={sectionStyles}>
          <h3 className={headingStyles}>Pre-Book Section</h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="prebookHeading" className={labelStyles}>Heading</label>
              <input
                type="text"
                id="prebookHeading"
                className={inputStyles}
                value={pageContent?.prebookSection?.heading || ''}
                onChange={(e) => handleChange('prebookSection', 'heading', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="prebookTagline" className={labelStyles}>Tagline</label>
              <input
                type="text"
                id="prebookTagline"
                className={inputStyles}
                value={pageContent?.prebookSection?.tagline || ''}
                onChange={(e) => handleChange('prebookSection', 'tagline', e.target.value)}
              />
            </div>
          </div>
          <button
            onClick={() => handleSaveContent('prebookSection', pageContent.prebookSection)}
            className={`${saveButtonStyles} mt-8`}
          >
            <Save className="w-5 h-5 mr-2" /> Save Pre-Book Section
          </button>
        </div>

        <div className="flex justify-center mt-12">
          <button
            onClick={() => setEditingGodrejBarcaMsr(false)}
            className={closeButtonStyle}
          >
            <X className="w-5 h-5 mr-2" /> Close Editor
          </button>
        </div>
      </div>
    </div>
  );
};

export default GodrejBarcaMsrContentManager;